/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MANAGE_PAYSLIPS, VIEW_PAYSLIPS } from "../../service/apiUrl";
import { postApiCall } from "../../service/PostApiCall";
import { Link, useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { withLightAppearance } from "../../utility/theme";
import Utils from "../../utility/utility";
import LoadingButton from "../../components/LoadingButton";
import PayslipDocument from "./PayslipDocument";
import {
  MAX_MONTHS,
  monthOptions,
  payslipsInRange,
  pdfFilename,
  periodKey,
  saveElementsAsPdf,
} from "./payslipPdf";

const ViewPayslip = (props) => {
  const [payslipData, setPayslipData] = useState({});
  // every payslip of this employee, for the From/To month pickers
  const [employeePayslips, setEmployeePayslips] = useState([]);
  const [fromKey, setFromKey] = useState(null);
  const [toKey, setToKey] = useState(null);
  // the payslips being rendered off screen for the PDF capture
  const [pdfSlips, setPdfSlips] = useState([]);
  const pdfStageRef = useRef(null);
  const pdfRendered = useRef(null);

  const { PayslipId } = useParams();
  let componentRef = useRef(null);

  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;
  const uid = payslipData?.uid?.id;
  const employeeName = [payslipData?.first_name, payslipData?.last_name].filter(Boolean).join(" ");

  useEffect(() => {
    if (access_token && PayslipId) {
      let params = {
        id: PayslipId,
      };
      postApiCall(params, access_token, VIEW_PAYSLIPS)
        .then((result) => {
          setPayslipData(result);
          const key = periodKey(result);
          setFromKey(key);
          setToKey(key);
        })
        .catch((error) => {
          console.error("view-Payslip error", error);
        });
    }
  }, [PayslipId]);

  useEffect(() => {
    if (!access_token || !uid) return;
    postApiCall({ uid, page: 1, pageSize: 500 }, access_token, MANAGE_PAYSLIPS)
      .then((result) => setEmployeePayslips(result?.payslips || []))
      .catch((error) => console.error("employee-Payslips error", error));
  }, [uid]);

  const months = useMemo(() => monthOptions(employeePayslips), [employeePayslips]);
  // "To" can only reach MAX_MONTHS - 1 months past "From"
  const toChoices = months.filter(
    (option) => fromKey !== null && option.key >= fromKey && option.key < fromKey + MAX_MONTHS
  );

  const changeFrom = (key) => {
    setFromKey(key);
    if (toKey === null || toKey < key || toKey >= key + MAX_MONTHS) setToKey(key);
  };

  // Once the off-screen slips have painted, the effect below resolves this.
  useEffect(() => {
    if (pdfSlips.length && pdfRendered.current) {
      const resolve = pdfRendered.current;
      pdfRendered.current = null;
      requestAnimationFrame(() => resolve());
    }
  }, [pdfSlips]);

  const downloadPdf = async () => {
    const range = payslipsInRange(months, fromKey, toKey);
    // the list has not loaded (or failed): this payslip alone is still known
    const ids = range.length ? range.map((option) => option.id) : [String(payslipData?.id)];
    const slips = await Promise.all(
      ids.map((id) =>
        id === String(payslipData?.id)
          ? payslipData
          : postApiCall({ id }, access_token, VIEW_PAYSLIPS)
      )
    );
    const painted = new Promise((resolve) => {
      pdfRendered.current = resolve;
    });
    setPdfSlips(slips);
    await painted;
    try {
      await withLightAppearance(() =>
        saveElementsAsPdf(
          [...pdfStageRef.current.querySelectorAll(".payslip-document")],
          pdfFilename(employeeName, range.length ? range[0].key : fromKey, range.length ? range[range.length - 1].key : toKey)
        )
      );
    } finally {
      setPdfSlips([]);
    }
    const span = toKey - fromKey + 1;
    if (range.length && range.length < span) {
      Utils.toast("success", `${range.length} of ${span} months have a payslip; the PDF has those ${range.length}.`);
    }
  };

  return (
    <div className={props?.data?.transtion == true ? "contents" : "content"}>
      <div className="content-top-spacer"></div>
      <div className="color-body manage">
        <div className="color-body pay-slip">
          <Link to="/manage-payslip" className="payslip-back">
            <i className="fa fa-arrow-left" aria-hidden="true"></i>
            Back to payslips
          </Link>
          <div className="mt-3 payslip-view-head">
            <h3>{employeeName}</h3>
            <div className="payslip-download">
              <label className="payslip-download-field">
                <span>From</span>
                <select
                  className="form-select"
                  value={fromKey ?? ""}
                  disabled={!months.length}
                  onChange={(e) => changeFrom(Number(e.target.value))}
                >
                  {months.length ? null : <option value={fromKey ?? ""}>…</option>}
                  {months.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="payslip-download-field">
                <span>To</span>
                <select
                  className="form-select"
                  value={toKey ?? ""}
                  disabled={!months.length}
                  onChange={(e) => setToKey(Number(e.target.value))}
                >
                  {toChoices.length ? null : <option value={toKey ?? ""}>…</option>}
                  {toChoices.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <ReactToPrint
                trigger={() => (
                  <button type="button" className="create-button1">
                    Print
                  </button>
                )}
                content={() => componentRef.current}
              />
              <LoadingButton
                className="create-button"
                successLabel="Downloaded"
                disabled={fromKey === null}
                onAction={downloadPdf}
              >
                {fromKey !== null && toKey !== null && toKey > fromKey
                  ? `Download PDF (${toKey - fromKey + 1} months)`
                  : "Download PDF"}
              </LoadingButton>
            </div>
          </div>
          <PayslipDocument ref={componentRef} payslip={payslipData} />
          <div className="payslip-pdf-stage" ref={pdfStageRef} aria-hidden="true">
            {pdfSlips.map((slip) => (
              <PayslipDocument key={slip?.id} payslip={slip} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPayslip;
